import { useChannelTalk } from '@repo/channel-talk';
import { useUserEventLogger } from '@repo/tracking-service';
import { Alert, AlertTitle, BoxButton, Description, Divider, List, semanticColor, Svg, Table, TableCell } from '@repo/ui';
import { useRouter } from 'next/router';

import { ResultTableRow } from '@/components/tax-refund/apply/result/ResultTableRow';
import ApplyResultFaqView from '@/components/tax-refund/common/view/ApplyResultFaqView';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { ApplyResultData } from '@/lib/types/request';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyResultRefundCompleteContent = ({ data, channelTalkLink }: { data: ApplyResultData; channelTalkLink: string }) => {
  const isCorps = data.businessType === 'corps';
  const displayName = isCorps ? (data.fullName ?? data.name) : data.name;
  const { openChannelTalk } = useChannelTalk();
  const { sendClickEvent } = useUserEventLogger();
  const router = useRouter();
  const { hasRegisteredCard, cardName, cardLogo, cardNumber } = data.paymentCard;

  const description = isCorps
    ? `’${displayName}’ 대표님, 이용료를 확인하고 결제해주세요.`
    : `${displayName}님, ${hasRegisteredCard ? '미리 등록한 카드로 결제할게요.' : '이용료를 확인하고 결제해주세요.'}`;
  const faqContents = [
    {
      title: '신청했던 환급금과 실제 입금된 금액이 달라요.',
      content:
        '환급금은 연도별로 분할되어 입금됩니다. 연도별 환급금은 각각 다른 날짜로 입금될 수 있고, 입금된 총 환급액은 연도별로 이자가 더해져 결정 환급액보다 클 수 있습니다. 자세한 사항이 궁금하시다면 고객센터로 문의해주세요.'
    },
    {
      title: '이용료가 30%가 넘는 것 같아요.',
      content: '이용료는 제휴 세무사 비용으로 결정 환급액의 30%(부가가치세 별도)입니다. 결제 금액은 부가세가 포함된 금액으로 안내드립니다.'
    },
    {
      title: '이용료를 먼저 결제해야 입금이 되나요?',
      content: '비즈넵 환급에서는 환급이 완료된 후 결제 안내를 드립니다. 환급금 확인이 되지 않으시면 고객센터로 문의해주세요.'
    },
    {
      title: '환급금이 분할 입금되나요?',
      content: '환급금은 연도별로 분할되어 입금됩니다. 연도별 환급금은 각각 다른 날짜로 입금될 수 있습니다.'
    },
    ...(isCorps
      ? [
          {
            title: '결제는 어떻게 하나요?',
            content:
              '아래 [이용료 결제 안내]를 눌러 안내된 계좌로 입금해주시거나, 카카오톡 알림톡이나 문자 메시지로 수신하신 결제 안내에 따라 결제를 진행해주세요.'
          },
          {
            title: '이용료에 대해 세금계산서를 발행하고 싶어요.',
            content: '이용료에 대한 세금계산서는 환급이 완료될 때 자동으로 발행되었습니다.'
          }
        ]
      : [
          {
            title: '결제는 어떻게 하나요?',
            content:
              '아래 [이용료 결제 안내]를 눌러 안내된 계좌로 입금해주시거나, 카카오톡 알림톡이나 문자 메시지로 수신하신 결제 안내에 따라 결제를 진행해주세요.'
          },
          {
            title: '현금영수증은 어떻게 발급하나요?',
            content:
              '계좌 입금이 완료되면 자진번호로 현금영수증을 발급한 후 카카오톡 알림톡으로 발급 정보를 전달해드립니다. 홈택스에서 발급정보로 현금영수증을 등록해주세요.'
          }
        ]),
    {
      title: '이용료 할부 결제가 가능한가요?',
      content:
        '카드 결제 시 결제 금액이 200,000원 이상인 경우 할부 결제가 가능합니다. 할부 결제가 필요하시다면 카카오톡 알림톡이나 문자 메시지로 수신하신 결제 링크에서 [카드사 앱으로 결제하기]를 눌러 결제를 진행해주세요.'
    },
    {
      title: '환급금이 입금되지 않았어요.',
      content: '만약 환급금이 아직 입금되지 않았다면 고객센터로 문의해주세요.'
    }
  ];
  const infoContents = [
    { content: '환급금은 국세청 또는 관할세무서명으로 입금돼요.' },
    ...(hasRegisteredCard && !isCorps
      ? [{ content: '환급이 완료되면 등록된 결제 카드로 이용료가 자동 결제돼요.' }]
      : [{ content: '최초 결제 안내일로부터 3일 이내에 결제 완료해주세요.' }, { content: '이용료 납부 기한이 경과되면 12% 가산 청구됩니다.' }]),
    { content: '환급금은 국세 체납이 있는 경우 국세에 자동 충당돼요.' }
  ];

  const handleAsk = () => {
    if (isCorps) {
      sendClickEvent('corps-result_cx_no-income');
    }
    openChannelTalk(channelTalkLink);
  };

  const handleCardClick = () => {
    if (isCorps) return;
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const paymentValue = data.paymentAmount === '' ? '확인중...' : data.paymentAmount;

  return (
    <div>
      <div className={'px-4.5 py-normal-small'}>
        <Description
          title={`환급금 잘 받으셨나요?\n이제 이용료 결제만 남았어요`}
          size={'medium'}
          description={description}
          graphic={<Svg src="icg_receipt_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>{isCorps ? '법인세 결정 환급액' : '종합소득세 결정 환급액'}</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{data.refundAmount}</div>
          <Divider className={'my-normal-medium'} />
          <ResultTableRow label={'환급 신청일'} value={data.applyDate} />
          <ResultTableRow label={'환급 결정일'} value={data.resultDate} />
          {!isCorps && <ResultTableRow label={'입금 예정일'} value={data.depositExpectDate} />}
          <ResultTableRow label={'환급 계좌'} value={formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)} />
          <ResultTableRow label={'이용료'} value={paymentValue} emphasize={data.paymentAmount !== ''} />
          {data.discountRate !== '0' ? (
            <Table
              headerCell={<TableCell variant={'header'}>이용료 할인</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className={'mb-1'}>제휴 할인 {data.discountRate}% 적용</p>
                    <p className={'text-13 text-gray-500'}>
                      결정 환급액의 <span className={'line-through'}>{data.defaultFeeRate}%</span>-&gt;{data.feeRate}
                      %(vat별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          ) : (
            <></>
          )}
          {!isCorps && hasRegisteredCard && (
            <Table
              headerCell={<TableCell variant={'header'}>결제 카드</TableCell>}
              contentCell={
                <>
                  <TableCell className={'flex items-center flex-1 justify-end'}>
                    <div className={'flex gap-tiny-medium text-[15px]'}>
                      <Svg src={cardLogo ?? ''} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                  </TableCell>
                  <TableCell>
                    <BoxButton size={'small'} className={'ml-3'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </TableCell>
                </>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />
        <div onClick={handleAsk} className={'pb-normal-large'}>
          <Alert
            size={'small'}
            variant={'custom'}
            bg={semanticColor.neutral.background.lowest}
            graphic={<Svg src={'icg_info_sm.svg'} width={20} height={20} />}
          >
            <AlertTitle>
              입금이 확인되지 않으면 <span className={'text-info-foreground-main underline'}>고객센터 문의</span>해주세요.
            </AlertTitle>
          </Alert>
        </div>
        <Divider />
        <div className={'pt-tiny-large'}>
          <List
            size={'small'}
            title={'환급이 결정되어 취소할 수 없어요.'}
            primaryAction
            state={'disabled'}
            supportingElement={
              <div className={'flex items-center opacity-40 body-small-medium'}>
                신청 취소
                <Svg src={'ic_chevron-right.svg'} width={18} height={18} />
              </div>
            }
          />
        </div>
      </div>
      <Divider size={'large'} />
      <ApplyResultFaqView data={faqContents} clickEventName={`${isCorps ? 'corps' : 'indis'}-result_faq_refund-completed`} />
      <div className={'pb-normal-medium'} />
    </div>
  );
};
